import { getAddressGeoLevel } from "../../auth";
import {
  fold, compactPostal, countryCodeOf, countryNameOf, NO_POSTAL_CODE_COUNTRIES,
  isPostalCodeRequired, stateKindOf, postalKindOf,
} from "./addressCountryRules";

// Geographic data + country rules shared by every address form of Elakay
// (AddressLocationSelector / useAddressLocation). One source: the server's
// data/countries/zipcodes.<cc>.json through GET /user/countries (the files
// My Profile's ZIP auto-fill and Target Location already used) -- the
// frontend never holds an API key or its own copy of the places.

export {
  fold, compactPostal, countryCodeOf, countryNameOf, NO_POSTAL_CODE_COUNTRIES,
  isPostalCodeRequired, stateKindOf, postalKindOf,
};

// ── Cached requests ─────────────────────────────────────────────────────
// A successful answer is kept for the session (the data never changes while
// the app runs); a failed one is not, so a later try can succeed.
const MAX_ENTRIES = 300;
const cache = new Map();
export const __resetAddressLocationCache = () => cache.clear();

const request = (country, level, params = {}) => {
  const code = countryCodeOf(country);
  if (!code) return Promise.resolve({ error: "No country" });
  const key = JSON.stringify([code, level, params.state || "", params.city || "", params.zipcode || ""]);
  if (cache.has(key)) return cache.get(key);
  if (typeof getAddressGeoLevel !== "function") return Promise.resolve({ error: "Unavailable" });
  const promise = Promise.resolve()
    .then(() => getAddressGeoLevel(code, level, params))
    .then((data) => {
      if (!data || data.error || data.aborted) {
        cache.delete(key);
        return { error: data?.error || "Unavailable" };
      }
      return data;
    })
    .catch(() => {
      cache.delete(key);
      return { error: "Network error" };
    });
  cache.set(key, promise);
  while (cache.size > MAX_ENTRIES) cache.delete(cache.keys().next().value);
  return promise;
};

// { available, zipLengths, hasStates } | { error }
export const loadCountryMeta = (country) => request(country, "meta");
// { available, items } | { error }
export const loadStates = (country) => request(country, "states");
export const loadCities = (country, state) => request(country, "cities", { state: state || "" });
export const loadZipCodes = (country, state, city) => request(country, "zipcodes", { state: state || "", city: city || "" });
// { available, matches, partial } | { error }
export const lookupPostalCode = (country, postalCode) => {
  const zipcode = compactPostal(postalCode);
  if (!zipcode) return Promise.resolve({ available: true, matches: [], partial: false });
  return request(country, "postal", { zipcode });
};

// ── Field-name adapters ─────────────────────────────────────────────────
// Forms keep their own field names (address book: State/postalCode, Stripe:
// state/postal_code, Target Location: stateOrProvince ...). The selector
// works on { country, state, stateCode, city, postalCode, latitude, longitude }.
export const ADDRESS_BOOK_FIELDS = { country: "country", state: "State", city: "city", postalCode: "postalCode", latitude: "latitude", longitude: "longitude" };
export const STRIPE_FIELDS = { country: "country", state: "state", city: "city", postalCode: "postal_code" };

export const toLocationValue = (source = {}, fields = ADDRESS_BOOK_FIELDS) => ({
  country: countryCodeOf(source[fields.country]) || "",
  state: source[fields.state] || "",
  stateCode: fields.stateCode ? source[fields.stateCode] || "" : "",
  city: source[fields.city] || "",
  postalCode: source[fields.postalCode] || "",
  latitude: fields.latitude ? source[fields.latitude] ?? null : null,
  longitude: fields.longitude ? source[fields.longitude] ?? null : null,
});

// Writes the location back onto the form object, leaving every other field
// (name, street, phone, ids ...) exactly as it was.
export const fromLocationValue = (target = {}, location = {}, fields = ADDRESS_BOOK_FIELDS) => {
  const next = { ...target };
  next[fields.country] = location.country || "";
  next[fields.state] = location.state || "";
  next[fields.city] = location.city || "";
  next[fields.postalCode] = location.postalCode || "";
  if (fields.stateCode) next[fields.stateCode] = location.stateCode || "";
  if (fields.latitude) next[fields.latitude] = location.latitude ?? null;
  if (fields.longitude) next[fields.longitude] = location.longitude ?? null;
  return next;
};
